import type {
  APIBookingDraft,
  APIBookingDraftBusServiceOption,
  APIBookingDraftCarRentalServiceOption,
  APIBookingDraftClothingServiceOption,
  APIBookingDraftLuggageServiceOption,
  APIBookingDraftParticipant,
  APIBookingDraftService,
  APIBookingDraftServiceCancellationInsuranceOption,
  APIBookingDraftServiceOption,
  APIBookingDraftServiceOptionParticipantPrice,
  APIBookingDraftSkiPassServiceOption,
  ServiceRenderType,
} from '@/scopes/checkout/infrastructure/api/types';
import type { CheckoutParticipantAgeCategory, CheckoutServiceOption } from '../../../domain/types';

export interface ServiceOptionMapper {
  map: (
    option:
      | APIBookingDraftServiceOption
      | APIBookingDraftLuggageServiceOption
      | APIBookingDraftServiceCancellationInsuranceOption
      | APIBookingDraftBusServiceOption
      | APIBookingDraftCarRentalServiceOption
      | APIBookingDraftSkiPassServiceOption
      | APIBookingDraftClothingServiceOption,
    service: APIBookingDraftService,
    bookingDraft: APIBookingDraft
  ) => CheckoutServiceOption;
}

export const createServiceOptionMapper = () => {
  function map(
    { displayText, bestValueText, code, price, prices, percentage }: APIBookingDraftServiceOption,
    { isPricePerPerson, renderType }: APIBookingDraftService,
    bookingDraft: APIBookingDraft
  ) {
    return {
      label: displayText,
      value: code,
      price: mapOptionPrice(price, prices ?? [], isPricePerPerson ?? false, renderType),
      rawPrice: price,
      percentage: percentage ?? undefined,
      ...(mapPriceBreakdown(renderType, bookingDraft.participants ?? [], prices ?? []) || undefined),
      ...(bestValueText && { badge: bestValueText }),
    };
  }

  function mapOptionPrice(
    price: number,
    prices: APIBookingDraftServiceOptionParticipantPrice[],
    isPricePerPerson: boolean,
    renderType: ServiceRenderType
  ): number {
    const pricesWithParticipantsFiltered = prices.filter((itemPrice) => itemPrice.participantIds.length > 0);

    if (
      !isPricePerPerson ||
      (isPricePerPerson && prices.length === 0) ||
      (renderType === 'PerParticipant' &&
        isPricePerPerson &&
        prices.length > 0 &&
        pricesWithParticipantsFiltered.length <= 1)
    ) {
      return price;
    }

    return prices?.reduce((total, item) => total + item.price * item.participantIds.length, 0);
  }

  function mapPriceBreakdown(
    renderType: ServiceRenderType,
    participants: APIBookingDraftParticipant[],
    prices: APIBookingDraftServiceOptionParticipantPrice[]
  ) {
    const pricesWithParticipantsFiltered = prices.filter((itemPrice) => itemPrice.participantIds.length > 0);

    if (
      renderType !== 'PerParticipant' ||
      !prices ||
      prices.length === 0 ||
      participants.length === 0 ||
      (renderType === 'PerParticipant' && prices.length > 0 && pricesWithParticipantsFiltered.length === 1)
    ) {
      return;
    }

    const ageCategoryPrices = {} as Record<CheckoutParticipantAgeCategory, number>;

    prices.forEach(({ price, participantIds }) => {
      participantIds.forEach((id) => {
        const participant = participants.find((p) => p.id === id);
        const formattedAgeCategory = participant?.ageCategory.toLowerCase() as CheckoutParticipantAgeCategory;
        if (participant && !ageCategoryPrices[formattedAgeCategory]) {
          ageCategoryPrices[formattedAgeCategory] = price;
        }
      });
    });

    const priceBreakdown = Object.entries(ageCategoryPrices).map(([ageCategory, price]) => ({
      ageCategory: ageCategory as CheckoutParticipantAgeCategory,
      price,
    }));

    return { priceBreakdown };
  }

  return { map };
};
